import { Link } from "react-router-dom";
import styled from "styled-components";

import { SiteNav, SiteFooter } from "../../components/fragments/ProductChrome";
import { Container } from "../../components/styles/Layout";
import { useStaticSeo } from "../../hooks/useSeo";
import { PRODUCT_SEO } from "../../data/seo";

/*
 * Doorstep Data's privacy policy.
 *
 * WHY A SEPARATE ONE. The agency's policy at property-people.co.uk covers
 * lettings enquiries, valuations, marketing consent and a contact form -
 * none of which exists here - and says nothing about the Android app. A
 * policy that describes the wrong product is worse than a short one that
 * describes the right one, because the parts a reader can check are the
 * parts they use to judge the parts they cannot.
 *
 * EVERY CLAIM IS CHECKABLE AGAINST THE CODE, and the comments say where.
 * The honest position here is unusually strong - no accounts, no cookies,
 * no third-party requests - so there is no reason to reach for the vague
 * phrasing these documents usually use. Where something IS retained (the
 * nginx log) it says so plainly rather than hiding behind "we may collect
 * certain technical information".
 *
 * IF THE BEHAVIOUR CHANGES, THIS CHANGES. Adding analytics, a font CDN, an
 * embedded map or an email capture would each falsify a sentence below. So
 * would uploading anything the app's camera produces: the claim that
 * photographs and floor plans never leave the phone is true only while the
 * report lookup remains the app's only network call.
 *
 * The camera paragraphs were added on 15 October 2026, when the app gained
 * room photography. They were overdue by a release - the floor plan had been
 * using the camera already, and this said nothing about it.
 *
 * THE SAVED-PROPERTIES PARAGRAPHS, 23 October 2026, and the second of them is
 * the one that matters. The app can now file a report, its rooms and a note of
 * its photographs against an address, and android:allowBackup is true with
 * only the install identifier excluded - so Android's own backup copies all of
 * that to the reader's Google Drive.
 *
 * That is fine, and it is what lets somebody's morning of measuring survive a
 * new handset, which backup_rules.xml argues for explicitly. What was not fine
 * was this page saying none of it is uploaded "to anyone else" while a file
 * listing every address somebody had saved went to Drive nightly. A floor plan
 * is a polygon; a saved property is a record of where a person has been.
 *
 * So the page now says so, names whose Drive it goes to, and says where to
 * switch it off. If the backup rules are ever narrowed to exclude
 * files/properties, this paragraph has to change with them - in that
 * direction it would be this page overstating what leaves the phone, which is
 * the better way round but still wrong.
 */

const Head = styled.header`
    background: #0f4c5c;
    color: #fff;
    padding: 2.75rem 0 2.25rem;
    h1 {
        margin: 0 0 0.4rem;
        font-size: 1.9rem;
        @media (max-width: 560px) {
            font-size: 1.55rem;
        }
    }
    p {
        margin: 0;
        opacity: 0.85;
        font-size: 0.95rem;
    }
`;

const Body = styled.div`
    max-width: 46rem;
    padding: 2rem 0 1rem;
    color: #3d4a4f;
    line-height: 1.65;
    h2 {
        font-size: 1.15rem;
        color: #0f4c5c;
        margin: 2rem 0 0.5rem;
    }
    h2:first-of-type {
        margin-top: 0;
    }
    p {
        margin: 0 0 0.9rem;
    }
    ul {
        margin: 0 0 0.9rem;
        padding-left: 1.15rem;
    }
    li {
        margin-bottom: 0.4rem;
    }
    strong {
        color: #26343a;
    }
`;

const Summary = styled.div`
    border: 1px solid #cfe0e5;
    border-left: 4px solid #0f4c5c;
    background: #f4f9fa;
    border-radius: 4px;
    padding: 1rem 1.15rem;
    margin: 0 0 1.75rem;
    p {
        margin: 0 0 0.5rem;
    }
    p:last-child {
        margin: 0;
    }
`;

export default function DoorstepPrivacy() {
    useStaticSeo(PRODUCT_SEO["/privacy-policy"]);

    return (
        <>
            <SiteNav />
            <Head>
                <Container>
                    <h1>Privacy policy</h1>
                    <p>Doorstep Data &middot; last updated 25 October 2026</p>
                </Container>
            </Head>

            <Container>
                <Body>
                    <Summary>
                        <p>
                            <strong>The short version.</strong> You do not need an account. We set
                            no cookies. The site loads nothing from anyone else &mdash; no
                            analytics, no trackers, no embedded fonts or maps. We do not sell or
                            share anything about you, and we never send you marketing.
                        </p>
                        <p>
                            The one thing we do keep is a standard web server log, which includes
                            your IP address, for 14 days.
                        </p>
                    </Summary>

                    <h2>Who is responsible</h2>
                    <p>
                        Doorstep Data is a trading name of <strong>Property People London Ltd</strong>,
                        a company registered in England and Wales (No. 07355433) at 204 Tooting High
                        Street, London, SW17 0SG. We are the data controller, and we are registered
                        with the Information Commissioner&rsquo;s Office under reference ZA005338.
                    </p>
                    <p>
                        For anything in this policy, email{" "}
                        <a href="mailto:asad@property-people.co.uk">asad@property-people.co.uk</a>,
                        or see the <Link to="/contact">contact page</Link>.
                    </p>

                    <h2>What happens when you run a report</h2>
                    <p>
                        You type a postcode and pick an address. We send those to our own server,
                        which queries public registers &mdash; HM Land Registry, the EPC register,
                        the Environment Agency, Ofcom, the Department for Education and Ofsted,
                        data.police.uk, the Planning Data Platform, the Food Standards Agency and
                        Transport for London &mdash; and returns the results to your browser.
                    </p>
                    <p>
                        Those services see a request from <strong>our</strong> server, not from you.
                        We do not pass on your IP address, or anything else about you, to any of
                        them.
                    </p>
                    <p>
                        We do not keep your report. Nothing you look up is stored against you,
                        because there is no &ldquo;you&rdquo; to store it against &mdash; there are
                        no accounts.
                    </p>

                    <h2>What we do keep</h2>
                    <ul>
                        <li>
                            <strong>A web server log</strong>, which records the IP address, time,
                            and web address of each request, as essentially every web server does.
                            It is kept for <strong>14 days</strong> and then deleted automatically.
                            We use it to keep the service working and to investigate abuse.
                        </li>
                        <li>
                            <strong>A short-lived rate-limiting counter</strong> held in memory,
                            which remembers that a request came from your address for up to{" "}
                            <strong>one minute</strong>, so that one person cannot overwhelm the
                            service. It is never written to disk.
                        </li>
                    </ul>
                    <p>
                        The postcodes and addresses you look up are{" "}
                        <strong>deliberately stripped</strong> from the application log before it is
                        written. The web server&rsquo;s own log, which is the one kept for 14 days,
                        does record the full web address of a request.
                    </p>

                    <h2>Cookies and storage</h2>
                    <p>
                        <strong>We set no cookies at all</strong>, so there is no cookie banner to
                        dismiss. The site stores one thing in your browser: which sections of the
                        report you ticked, so the page remembers your choice next time. That stays
                        on your device, is never sent to us, and you can clear it with your
                        browser&rsquo;s normal &ldquo;clear site data&rdquo;.
                    </p>
                    <p>
                        <strong>Your browser makes no requests to any third party.</strong> There
                        are no trackers, no advertising, no analytics and no fonts or scripts loaded
                        from anyone else. You can check that yourself in your browser&rsquo;s
                        network inspector. The one place anything you give us reaches another
                        company is the valuation below, and only if you ask for one.
                    </p>

                    <h2>The valuation estimate</h2>
                    <p>
                        The report includes an optional valuation. It runs{" "}
                        <strong>only when you press the button</strong> &mdash; opening a report
                        never triggers it, and if you never press it, nothing in this section
                        applies to you.
                    </p>
                    <p>
                        When you do, our server sends Google&rsquo;s Gemini API the postcode, the
                        address and energy-certificate reference of the property you chose, the
                        comparable-sales figures we have already worked out from public records, and
                        anything you typed into the optional boxes &mdash; tenure, condition,
                        parking and so on. It comes back with a suggested adjustment, which is what
                        the page shows you.
                    </p>
                    <p>
                        Your browser does not talk to Google; our server does, so nothing identifies
                        you to them &mdash; no name, no address of yours, no identifier, no IP
                        address of yours. <strong>We do not store the request or the result.</strong>{" "}
                        The valuation is worked out, shown to you, and gone when you close the page;
                        it is not written to our database and the property details you typed are not
                        kept. They are never written to our logs either. Please do not type
                        anything into those boxes you would not want sent to Google.
                    </p>

                    <h2>If you ask us for a real valuation</h2>
                    <p>
                        Under the automated figure there is a form offering a proper valuation &mdash;
                        somebody standing in the property. It is <strong>entirely optional</strong>;
                        the report and the estimate work without it and nothing on this site
                        requires you to give us your name.
                    </p>
                    <p>
                        If you use it, we receive your name, email address, phone number if you give
                        one, and the address of the property you were looking at. We use them to
                        contact you about that valuation. Our lawful basis is your request &mdash;
                        you asked us to get in touch &mdash; and you can tell us to stop at any time.
                    </p>
                    <p>
                        {/*
                         * Named, unbundled and reversible, because consent to
                         * pass somebody to a third party has to be a free and
                         * specific act of its own. The tick is separate, it
                         * starts empty, and the form works without it.
                         */}
                        The form has a <strong>separate tick box</strong>, empty unless you fill it
                        in, asking whether we may pass those same details to a{" "}
                        <strong>local estate agent</strong> so they can offer you a valuation. We do
                        not pass anything to anybody unless you tick it. If you do, we may be paid a
                        referral fee by that agent, and we say so beside the box. Leaving it unticked
                        is a normal way to use the form, not a lesser one: your enquiry comes to us
                        and goes no further.
                    </p>
                    <p>
                        You can withdraw that permission at any time by emailing us, and we will
                        tell the agent we passed you to. Withdrawing it does not undo an
                        introduction already made &mdash; once an agent has your details they are
                        responsible for them, under their own privacy policy &mdash; but it stops
                        any further sharing.
                    </p>

                    <h2>The Android app</h2>
                    <p>
                        The Doorstep Data Android app talks to the same service and keeps the same
                        rules. It generates a random identifier when you install it and sends it
                        with each request, so our rate limiting can tell one phone from another.
                        Mobile networks put thousands of people behind a handful of addresses, so
                        without it one person&rsquo;s usage could lock out a whole network.
                    </p>
                    <p>
                        That identifier is random, is not linked to you, your device, your Google
                        account or any advertising identifier, and is discarded if you uninstall the
                        app. The app contains no analytics and no advertising.
                    </p>
                    <p>
                        The app uses the camera in two places: to measure a room for a floor plan,
                        and to photograph rooms. Both run entirely on the phone. Photographs are
                        saved to your own gallery, and only the ones you choose to keep &mdash; the
                        rest are deleted without ever leaving the app. Floor plans are stored on the
                        device. <strong>None of it is sent to us</strong> and we never see it; see
                        the note below on Android&rsquo;s own backup, which is a separate matter and
                        not ours. The app asks for the camera at the moment you open one of those
                        two screens, so if you only read reports you are never asked at all.
                    </p>
                    <p>
                        The app can <strong>save a property to the phone</strong> &mdash; the report
                        as it was looked up, the rooms you measured, and a note of which photographs
                        belong to it. That is kept so you can read it again with no signal. It lives
                        in the app&rsquo;s own storage, it is not sent to us, and we never see it.
                        The photographs themselves stay in your gallery; the app only records which
                        ones belong to which property, so deleting a saved property removes our note
                        and leaves your pictures alone. Deleting the app removes all of it.
                    </p>
                    <p>
                        <strong>One thing worth knowing, because it is not us.</strong> If you have
                        Android&rsquo;s own backup switched on &mdash; most phones do, by default
                        &mdash; then Android copies saved properties and floor plans to{" "}
                        <em>your</em> Google Drive, the same way it backs up other apps. That means a
                        saved report, including the address it is about, can leave the handset. It
                        goes to your Google account and not to ours, we have no access to it, and it
                        is what lets your saved work follow you to a new phone. You can stop it in
                        Android under Settings &rarr; Google &rarr; Backup, either for everything or
                        for this app alone. The random identifier described above is the one thing we
                        exclude from that backup, so that it really is discarded on uninstall.
                    </p>
                    <p>
                        Looking up a report is the only thing the app sends anywhere, and the
                        postcode and address you look up are the only things it sends.
                    </p>

                    <h2>Why we are allowed to do this</h2>
                    <p>
                        Our lawful basis is <strong>legitimate interests</strong>: running a working
                        service, and protecting it from abuse. We have weighed that against your
                        privacy, which is why the logs are short-lived, the postcodes are stripped,
                        and there is nothing else.
                    </p>
                    <p>
                        The property information in a report comes from public registers published
                        under the Open Government Licence. Because it is tied to an address, some of
                        it can be personal data about the people who live there. We do not add to
                        it, combine it with anything else, or keep our own copy of a report.
                    </p>

                    <h2>Your rights</h2>
                    <p>
                        You have the right to ask what personal data we hold about you, to have it
                        corrected or erased, to object to our processing it, and to ask for a copy.
                        In practice we hold almost nothing and only for 14 days, so the honest
                        answer to most requests is that there is nothing to give you &mdash; but ask
                        and we will tell you.
                    </p>
                    <p>
                        If you are unhappy with how we have handled your personal data you can
                        complain to the Information Commissioner&rsquo;s Office at{" "}
                        <a
                            href="https://ico.org.uk/make-a-complaint/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            ico.org.uk/make-a-complaint
                        </a>
                        . We would rather you told us first, so we can put it right.
                    </p>

                    <h2>Changes</h2>
                    <p>
                        If we change what we collect, this page changes with it and the date at the
                        top moves. We are not going to quietly add tracking and leave this page
                        saying otherwise.
                    </p>
                </Body>
            </Container>
            <SiteFooter />
        </>
    );
}
